import React, { CSSProperties } from 'react'
import { LuArrowLeftRight, LuUserPlus } from 'react-icons/lu'
import { useNavigate } from 'react-router'
import { useKeyPressEvent } from 'react-use'

import compassIcon from '@/assets/img/icons/compass.svg'
import networkIcon from '@/assets/img/icons/network.svg'
import MobileMenuCloseButton from '@/components/misc/MobileMenuCloseButton'
import SvgImage from '@/components/misc/SvgImage'
import { INVITE_TO_RQ_MODAL_STEP } from '@/components/modals/InviteToRqModal/constants'
import { Card, CardContent } from '@/components/ui/card'
import { useSidebar } from '@/components/ui/sidebar'
import { useAddReferralsModal } from '@/hooks/useAddReferralsModal'
import { useInviteToRqModal } from '@/hooks/useInviteToRqModal'
import { useInviteUsersModal } from '@/hooks/useInviteUsersModal'
import { useIsMinWidthMd } from '@/hooks/useIsMinWidthMd'
import { usePrefetchReferralSetupQuery } from '@/hooks/usePrefetchReferralSetupQuery'
import { useQuickActionsMenu } from '@/hooks/useQuickActionsMenu'
import { THEME_PALETTE } from '@/lib/constants'
import { ROUTES } from '@/lib/routes'

interface MenuItem {
  icon: React.ReactNode | string
  title: string
  description: string
  onClick: () => void
  onMouseOver?: () => void
}

// @todo: Replace with shadcn's Dropdown component
const QuickActionsMenu: React.FC = () => {
  const navigate = useNavigate()
  const inviteToRqModal = useInviteToRqModal()
  const addReferralsModal = useAddReferralsModal()
  const inviteUsersModal = useInviteUsersModal()
  const quickActionsMenu = useQuickActionsMenu()
  const isMinWidthMd = useIsMinWidthMd()
  const isMobile = !isMinWidthMd
  const { setOpenMobile } = useSidebar()
  const prefetchReferralSetupQuery = usePrefetchReferralSetupQuery()

  const menuItems: MenuItem[] = [
    {
      icon: <LuArrowLeftRight className="size-4" />,
      title: 'New Referral',
      description: 'Send a new referral',
      onClick: () => {
        if (isMobile) {
          setOpenMobile(false)
        }
        navigate(ROUTES.referClient())
      },
      onMouseOver: prefetchReferralSetupQuery,
    },
    {
      icon: networkIcon,
      title: 'Invite relationship',
      description: 'Invite another firm or adviser',
      onClick: () =>
        inviteToRqModal.show(INVITE_TO_RQ_MODAL_STEP.referralPartners),
    },
    {
      icon: <LuArrowLeftRight className="size-4" />,
      title: 'Upload data',
      description: 'Add your existing referral data',
      onClick: addReferralsModal.showModal,
    },
    {
      icon: compassIcon,
      title: 'Start Compass',
      description: 'Invite your clients to complete Compass',
      onClick: () => navigate(ROUTES.companyCompass()),
    },
    {
      icon: <LuUserPlus className="size-4" />,
      title: 'Invite your team',
      description: 'Add colleagues to your account',
      onClick: inviteUsersModal.show,
    },
  ]

  // Hide menu on pressing the 'Escape' key
  useKeyPressEvent('Escape', quickActionsMenu.hide)

  const styles: CSSProperties = isMinWidthMd
    ? {
        position: 'absolute',
        minWidth: '300px',
        bottom: '0',
        left: '150%',
      }
    : {
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        paddingTop: quickActionsMenu.isShowing ? '45px' : 0,
      }

  return (
    <Card className="pop-in flex flex-col bg-white p-2" style={styles}>
      <MobileMenuCloseButton onClick={quickActionsMenu.hide} className="mb-4" />

      <CardContent className="p-0">
        {menuItems.map((item) => {
          const iconWrapperSize = 30

          return (
            <div
              key={item.title}
              className="hover:bg-navbar-blue flex cursor-pointer items-center
                rounded-lg p-2"
              onMouseOver={item.onMouseOver}
              onClick={() => {
                quickActionsMenu.hide()
                item.onClick()
              }}
              css={{
                filter: 'grayscale(1)',
                gap: '16px',
                color: THEME_PALETTE.white,
                '&:hover': {
                  filter: 'grayscale(0)',
                },
              }}
            >
              <div
                className="flex items-center justify-center rounded-full"
                style={{
                  backgroundColor: '#F4F4F4',
                  color: '#8D8D8D',
                  width: iconWrapperSize,
                  height: iconWrapperSize,
                }}
              >
                {typeof item.icon === 'string' ? (
                  <SvgImage src={item.icon} className="size-4" />
                ) : (
                  <>{item.icon}</>
                )}
              </div>

              <div>
                <div className="text-black">{item.title}</div>
                <div className="text-body text-xs font-light">
                  {item.description}
                </div>
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

export default QuickActionsMenu
